import { jsonPointerGet } from '@cotera/utilities';
// type-fest's JsonValue, for the same reason `bucket-flush-policy` uses it: the
// grouped output crosses into the server runner / kv layer, which is type-fest.
import type { JsonValue } from 'type-fest';

/**
 * A group-by node takes one array on its input and splits it into one entry per
 * distinct value of a field, so a downstream `for-each` can fan out over the
 * result — one branch per person, per account, per whatever the field names.
 *
 * It exists because a bucket collects into a single pile. The pile holds every
 * contributor's items mixed together, and until now nothing could take that
 * pile apart: a mapper renames fields, a filter drops whole branches, a for-each
 * iterates what it is given. None of them can say "split this on `/user_id`".
 *
 * Framework-free like the flush policy, so the server runner and the client
 * editor agree on what a group is rather than each deciding.
 */

/** Persisted group-by node `data`. */
export type GroupByConfig = {
  /**
   * RFC 6901 JSON Pointer to the array being split (`''` = the whole input).
   *
   * Almost always `/items`, since a bucket flush is what this reads. Kept
   * configurable rather than hard-wired to the flush envelope so the node also
   * works behind anything else that produces a list.
   */
  readonly itemsPath: string;
  /** JSON Pointer into each item for the value to group on (`''` = the whole item). */
  readonly keyPath: string;
  /**
   * The upstream node whose output `itemsPath` reads, named by its stable graph
   * id. Absent means the direct predecessor — the default and only option until
   * now. Set, it lets a group-by split a list produced several steps back, the
   * way a for-each points `itemsPath` at an ancestor via its own `sourceRunNodeId`.
   */
  readonly sourceRunNodeId?: string;
};

export type ParseGroupByConfigResult =
  | { readonly ok: true; readonly config: GroupByConfig }
  | { readonly ok: false; readonly error: string };

/** One group: the value it was split on, and the items that carried it. */
export type GroupByGroup = {
  /**
   * The key value as it appeared on the items, not a stringified form of it.
   *
   * A downstream branch writes this into a message ("Hi {{key}}"), so a number
   * has to arrive as a number and a string without quotes around it. The
   * canonical string used to decide sameness is an internal detail and never
   * leaves this module.
   */
  readonly key: JsonValue;
  readonly items: readonly JsonValue[];
  readonly count: number;
};

/** What a group-by node emits. */
export type GroupByOutput = {
  readonly groups: readonly GroupByGroup[];
  /** How many distinct keys were found, so a filter can branch without counting. */
  readonly groupCount: number;
  /** Items across all groups, so a downstream step can sanity-check the split. */
  readonly itemCount: number;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Whether a string is usable as a JSON Pointer here: rooted, or blank for "the
 * whole value". Exported because the editor warns on a bad path per field while
 * this module validates the config as a whole — two places applying the rule,
 * one place stating it.
 */
export function isJsonPointerPath(value: string): boolean {
  return value === '' || value.startsWith('/');
}

function readPointer(
  value: unknown,
  label: string
): { ok: true; pointer: string } | { ok: false; error: string } {
  if (value === undefined || value === null) {
    return { ok: true, pointer: '' };
  }
  if (typeof value !== 'string') {
    return { ok: false, error: `Group by ${label} must be a field path.` };
  }
  const pointer = value.trim();
  if (!isJsonPointerPath(pointer)) {
    return {
      ok: false,
      error: `Group by ${label} must be a JSON Pointer ("/field") or blank.`,
    };
  }
  return { ok: true, pointer };
}

/**
 * Validates persisted group-by node `data`, or returns a message the step can
 * fail with. Every error names the field and what was wrong with it, because it
 * is the only thing the person fixing it will see.
 */
export function parseGroupByConfig(data: unknown): ParseGroupByConfigResult {
  const record = asRecord(data);
  if (record === null) {
    return { ok: false, error: 'Group by node is missing configuration.' };
  }

  const items = readPointer(record['itemsPath'], 'list');
  if (!items.ok) {
    return { ok: false, error: items.error };
  }
  const key = readPointer(record['keyPath'], 'field');
  if (!key.ok) {
    return { ok: false, error: key.error };
  }
  // The key is the whole point of the node. Without one there is nothing to
  // split on, and grouping every item under a single `null` key would look like
  // it worked while producing exactly the pile it was asked to break up.
  if (key.pointer === '') {
    return {
      ok: false,
      error: 'Group by needs a field to group on.',
    };
  }

  // A node id, not a pointer — a non-empty string names the source step, and
  // anything else (blank, absent, wrong type) means "the direct predecessor".
  const rawSource = record['sourceRunNodeId'];
  const sourceRunNodeId =
    typeof rawSource === 'string' && rawSource.trim() !== ''
      ? rawSource.trim()
      : undefined;

  return {
    ok: true,
    config: {
      itemsPath: items.pointer,
      keyPath: key.pointer,
      ...(sourceRunNodeId !== undefined ? { sourceRunNodeId } : {}),
    },
  };
}

/**
 * The string two key values are compared by.
 *
 * `JSON.stringify` rather than `String()`, matching the bucket's `distinct`
 * reducer, so the two never disagree about how many distinct values a batch
 * held. It also keeps `1` and `"1"` apart, which `String()` would merge — two
 * different customer ids reading as one account is not a mistake worth being
 * lenient about.
 */
function canonicalKey(value: unknown): string {
  return JSON.stringify(value ?? null);
}

/**
 * Splits `items` on `keyPath`.
 *
 * Groups come back in the order their key was first seen, and items keep their
 * order within a group. That is not cosmetic: a fan-out over these runs one
 * branch per group, and a stable order is what makes a re-run of the same input
 * produce the same sequence of branches.
 *
 * An item whose key is missing groups under `null` rather than being dropped.
 * Silently discarding it would lose data the bucket had already accepted, and
 * a `null` group is visible to whoever has to explain the extra branch.
 */
export function groupItemsByKey(params: {
  readonly items: readonly JsonValue[];
  readonly keyPath: string;
}): GroupByOutput {
  const { items, keyPath } = params;
  const byKey = new Map<string, { key: JsonValue; items: JsonValue[] }>();

  for (const item of items) {
    const raw = jsonPointerGet(item, keyPath);
    const key = (raw ?? null) as JsonValue;
    const canonical = canonicalKey(raw);
    const existing = byKey.get(canonical);
    if (existing === undefined) {
      byKey.set(canonical, { key, items: [item] });
    } else {
      existing.items.push(item);
    }
  }

  const groups: GroupByGroup[] = [...byKey.values()].map((entry) => ({
    key: entry.key,
    items: entry.items,
    count: entry.items.length,
  }));

  return {
    groups,
    groupCount: groups.length,
    itemCount: items.length,
  };
}
